// Virtual net billing: suggested allocation percentages.
//
// A button sends the supplies' hourly consumption (as edited on the page),
// their voltage reduction factors, the current percentages and the
// connection limit; the backend nets them against the project's stored
// reference-year PV production and returns the percentages that net the most
// energy in a year (backend virtual_net_billing.suggest_allocation). Nothing
// changes until the user applies them; nothing is stored and no study is
// counted.
import { useState } from "react";
import { Alert, Box, Button, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { metenApi } from "../services/metenApi";
import { useOptionalFile } from "../context/FileContext";
import { useTranslateText } from "../context/LanguageContext";
import {
  VNB_SUGGEST_APPLIED,
  VNB_SUGGEST_APPLY,
  VNB_SUGGEST_BEST_ALREADY,
  VNB_SUGGEST_BUTTON,
  VNB_SUGGEST_COL_CURRENT,
  VNB_SUGGEST_COL_NETTED_CURRENT,
  VNB_SUGGEST_COL_NETTED_SUGGESTED,
  VNB_SUGGEST_COL_SUGGESTED,
  VNB_SUGGEST_ERROR,
  VNB_SUGGEST_GAIN,
  VNB_SUGGEST_KWP_NOTE,
  VNB_SUGGEST_NO_ESTIMATE,
  VNB_SUGGEST_NOTE,
  VNB_SUGGEST_RUNNING,
  VNB_SUGGEST_SAVE_FIRST,
  VNB_SUGGEST_TEXT,
  VNB_SUGGEST_TITLE,
  VNB_SUGGEST_TOTAL,
  VNB_SUPPLY_NAME_LABEL,
  hourlyValues,
  voltageLevels,
} from "./config/virtualNetBilling";

const mwh = (kwh) => (kwh == null ? "—" : (kwh / 1000).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
const pct = (value) => (value == null ? "—" : Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

function errorCode(error) {
  try {
    return JSON.parse(error?.responseText || "")?.detail?.code || null;
  } catch {
    return null;
  }
}

/** cfg: the project config; supplies: the VNB supplies; onApply(percentages). */
export default function VirtualNetBillingAllocation({ cfg, supplies, onApply }) {
  const file = useOptionalFile();
  const projectId = file?.currentProject?.id || null;
  const translateText = useTranslateText();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [problem, setProblem] = useState(null);
  const [applied, setApplied] = useState(false);
  const ready = supplies.length >= 2
    && supplies.every((s) => Array.isArray(s?.loadProfile?.values) && s.loadProfile.values.length);

  const calculate = async () => {
    if (!projectId) {
      setProblem(VNB_SUGGEST_SAVE_FIRST);
      return;
    }
    setBusy(true);
    setProblem(null);
    setApplied(false);
    try {
      const { factors } = voltageLevels(cfg?.btm?.virtualNetBilling);
      const exportLimit = Number(cfg?.grid?.exportLimitKw);
      const body = {
        supplies: supplies.map((s, i) => {
          const share = Number(String(s?.sharePct ?? "").replace(",", "."));
          return {
            name: String(s?.name || i + 1),
            hourly_kwh: hourlyValues(s.loadProfile),
            share_pct: Number.isFinite(share) && String(s?.sharePct ?? "") !== "" ? share : null,
            reduction_factor: factors[i] ?? 1,
          };
        }),
        export_limit_kw: Number.isFinite(exportLimit) && exportLimit > 0 ? exportLimit : null,
      };
      setResult(await metenApi.suggestVirtualNetBillingAllocation(projectId, body));
    } catch (error) {
      setResult(null);
      setProblem(errorCode(error) === "no_estimate" ? VNB_SUGGEST_NO_ESTIMATE : VNB_SUGGEST_ERROR);
    } finally {
      setBusy(false);
    }
  };

  const projectKwp = Number(cfg?.pvInstalledCap?.kw);
  const estimateKwp = Number(result?.estimate?.dc_capacity_kw);
  const sizeDiffers = result && projectKwp > 0 && estimateKwp > 0
    && Math.abs(estimateKwp - projectKwp) > 0.01 * projectKwp;
  const gain = result?.gain_kwh;

  return (
    <Box sx={{ mt: 2 }} data-testid="vnb-allocation">
      <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
        {VNB_SUGGEST_TITLE}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {VNB_SUGGEST_TEXT}
      </Typography>
      <Button size="small" variant="outlined" onClick={calculate} disabled={!ready || busy}
        startIcon={busy ? <CircularProgress size={14} /> : null} data-testid="vnb-allocation-calculate">
        {busy ? VNB_SUGGEST_RUNNING : VNB_SUGGEST_BUTTON}
      </Button>
      {problem && <Alert severity="info" sx={{ mt: 1.5 }}>{problem}</Alert>}
      {result && (
        <Box sx={{ mt: 1.5 }}>
          <Table size="small" data-testid="vnb-allocation-table">
            <TableHead>
              <TableRow>
                <TableCell>{VNB_SUPPLY_NAME_LABEL}</TableCell>
                <TableCell align="right">{VNB_SUGGEST_COL_CURRENT}</TableCell>
                <TableCell align="right">{VNB_SUGGEST_COL_SUGGESTED}</TableCell>
                <TableCell align="right">{VNB_SUGGEST_COL_NETTED_CURRENT}</TableCell>
                <TableCell align="right">{VNB_SUGGEST_COL_NETTED_SUGGESTED}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody data-no-translate="">
              {supplies.map((s, i) => (
                <TableRow key={s.id || i}>
                  <TableCell>{String(s?.name || i + 1)}</TableCell>
                  <TableCell align="right">{pct(result.current_pct?.[i])}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{pct(result.suggested_pct?.[i])}</TableCell>
                  <TableCell align="right">{mwh(result.current_netted_kwh?.[i])}</TableCell>
                  <TableCell align="right">{mwh(result.suggested_netted_kwh?.[i])}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>{translateText(VNB_SUGGEST_TOTAL)}</TableCell>
                <TableCell align="right">{result.current_pct ? "100.00" : "—"}</TableCell>
                <TableCell align="right">100.00</TableCell>
                <TableCell align="right">{mwh(result.current_netted_total_kwh)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{mwh(result.suggested_netted_total_kwh)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {gain != null && (
            <Typography variant="body2" sx={{ mt: 1 }} data-testid="vnb-allocation-gain">
              {gain > 0.5 ? (
                <>
                  <span>{VNB_SUGGEST_GAIN}</span>{": "}
                  <span data-no-translate="">{`+${mwh(gain)} MWh (+${(100 * gain / Math.max(1, result.current_netted_total_kwh)).toFixed(1)}%)`}</span>
                </>
              ) : (
                <span>{VNB_SUGGEST_BEST_ALREADY}</span>
              )}
            </Typography>
          )}
          {sizeDiffers && <Alert severity="warning" sx={{ mt: 1 }}>{VNB_SUGGEST_KWP_NOTE}</Alert>}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            {VNB_SUGGEST_NOTE}
          </Typography>
          <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Button size="small" variant="contained" disabled={applied || (gain != null && gain <= 0.5)}
              data-testid="vnb-allocation-apply"
              onClick={() => { onApply(result.suggested_pct); setApplied(true); }}>
              {VNB_SUGGEST_APPLY}
            </Button>
            {applied && <Typography variant="body2" color="success.main">{VNB_SUGGEST_APPLIED}</Typography>}
          </Box>
        </Box>
      )}
    </Box>
  );
}
